import { memo, useMemo } from "react"
import { useViewport, useStore } from "@xyflow/react"
import { useCursor } from "../../contexts/cursor"

export const ConnectionPreview = memo(function ConnectionPreview() {
	const { remoteCursors, peers } = useCursor()
	const viewport = useViewport()
	const nodes = useStore((state) => state.nodes)

	const connectingCursors = useMemo(() => {
		return Array.from(remoteCursors.values()).filter(
			(c) => c.state === "connecting" && c.connectingFrom,
		)
	}, [remoteCursors])

	if (connectingCursors.length === 0) return null

	return (
		<svg className="pointer-events-none absolute inset-0 overflow-visible">
			{connectingCursors.map((cursor) => {
				const { connectingFrom, position } = cursor
				if (!connectingFrom) return null

				const userInfo = peers.get(cursor.connectionId)
				const sourceNode = nodes.find((n) => n.id === connectingFrom.nodeId)
				if (!sourceNode) return null

				// Get node dimensions
				const nodeWidth = sourceNode.measured?.width ?? sourceNode.width ?? 280
				const nodeHeight =
					sourceNode.measured?.height ?? sourceNode.height ?? 100

				// Calculate handle position based on handle type
				// Source handles are on the right, target handles are on the left
				let handleX: number
				let handleY: number

				if (connectingFrom.handleType === "source") {
					// Right side of node
					handleX = sourceNode.position.x + nodeWidth
					handleY = sourceNode.position.y + nodeHeight / 2
				} else {
					// Left side of node - if connecting from a target, we're going backwards
					handleX = sourceNode.position.x
					handleY = sourceNode.position.y + nodeHeight / 2
				}

				// Transform to screen coordinates
				const startX = handleX * viewport.zoom + viewport.x
				const startY = handleY * viewport.zoom + viewport.y
				const endX = position.x * viewport.zoom + viewport.x
				const endY = position.y * viewport.zoom + viewport.y

				// Calculate control points for bezier curve
				const dx = endX - startX
				const controlOffset = Math.min(Math.abs(dx) * 0.5, 100)

				const color = userInfo?.color ?? "#888"

				return (
					<g key={`connection-${cursor.connectionId}`}>
						{/* Connection line */}
						<path
							d={`M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${endX - controlOffset} ${endY}, ${endX} ${endY}`}
							fill="none"
							stroke={color}
							strokeWidth="2"
							strokeDasharray="6 4"
							className="animate-pulse"
						/>
						{/* Start handle indicator */}
						<circle
							cx={startX}
							cy={startY}
							r="6"
							fill={color}
							stroke="white"
							strokeWidth="2"
						/>
						{/* End point indicator */}
						<circle cx={endX} cy={endY} r="4" fill={color} opacity="0.6" />
						{/* Label */}
						<text
							x={startX + 10}
							y={startY - 10}
							fill={color}
							fontSize="12"
							fontWeight="500"
						>
							{userInfo?.name ?? "User"} connecting...
						</text>
					</g>
				)
			})}
		</svg>
	)
})
